import { postForm } from '@/shared/api/client';
import { submitForm, submitFormData } from '@/shared/api/submit';
import { getListRows } from '@/shared/lib/pagination';
import type { PlaceItem } from '@/shared/api/places';

export type ListResponse = Record<string, unknown>;

export interface SeoListParams {
  page: number;
  sort_field: string;
  sort_order: string;
  id: string;
  name: string;
  is_seo_active?: 1;
}

export interface SeoPage extends Record<string, unknown> {
  id: number;
  title?: string;
  description?: string;
  short_description?: string;
  url?: string;
  seo_text?: string;
  selling_seo_text?: string;
}

export interface SeoCity extends Record<string, unknown> {
  id: number;
  name: string;
  country?: string;
  region?: string;
  is_seo_active?: number | boolean;
}

export interface SeoBrand extends Record<string, unknown> {
  id: number;
  name: string;
  type?: string;
  is_seo_active?: number | boolean;
}

export interface SeoModel extends Record<string, unknown> {
  id: number;
  name: string;
  mark?: string;
  is_seo_active?: number | boolean;
}

export interface SeoWorkType extends Record<string, unknown> {
  id: number;
  name: string;
  slug?: string;
  is_active?: number;
  parent_id?: number | null;
}

// ---- SEO pages -------------------------------------------------------------

export interface SeoPagesParams {
  page: number;
  sort: string;
  parent_id: string;
  model_id: string;
  mark_id: string;
  city_id: string;
  url: string;
}

export const fetchSeoPages = (params: SeoPagesParams) =>
  postForm<ListResponse>('/seo/page/get-list/', { ...params });

export async function fetchSeoPage(id: number) {
  const res = await postForm<{ seo_page?: SeoPage }>('/seo/page/view/', { id });
  if (!res.seo_page) throw new Error('SEO page not found');
  return res.seo_page;
}

export interface SeoPageFormValues {
  title: string;
  h1: string;
  description: string;
  sellingSeoText: string;
  seoText: string;
}

/** Built by hand (not `submitForm`) so empty strings reach the server and the operator can clear a field. */
export const saveSeoPage = (id: number, v: SeoPageFormValues) => {
  const body = new FormData();
  body.append('id', String(id));
  body.append('short_description', v.h1);
  body.append('title', v.title);
  body.append('description', v.description);
  body.append('selling_seo_text', v.sellingSeoText);
  body.append('seo_text', v.seoText);
  return submitFormData('/seo/page/save/', body);
};

async function fetchActiveSeoItems(url: string, field: string, key: string) {
  const res = await postForm<ListResponse>(url, { [field]: 1 });
  return getListRows<PlaceItem>(res, [key]);
}

export const fetchActiveSeoWorks = () =>
  fetchActiveSeoItems('/seo/work-type/get-list/', 'is_active', 'work_types');
export const fetchActiveSeoCities = () =>
  fetchActiveSeoItems('/seo/city/get-list/', 'is_seo_active', 'cities');
export const fetchActiveSeoMarks = () =>
  fetchActiveSeoItems('/seo/car/get-mark-list/', 'is_seo_active', 'marks');
export const fetchActiveSeoModels = () =>
  fetchActiveSeoItems('/seo/car/get-model-list/', 'is_seo_active', 'models');

// ---- sitemap participation lists (cities / brands / models) ----------------

export const fetchSeoCities = (params: SeoListParams & { country_id: string; region_id: string }) =>
  postForm<ListResponse>('/seo/city/get-list/', { ...params });

export const fetchSeoBrands = (params: SeoListParams & { type_id: string }) =>
  postForm<ListResponse>('/seo/car/get-mark-list/', { ...params });

export const fetchSeoModels = (params: SeoListParams & { mark_id: string }) =>
  postForm<ListResponse>('/seo/car/get-model-list/', { ...params });

export async function fetchCarTypes() {
  const res = await postForm<ListResponse>('/car/type/list/', {});
  return getListRows<PlaceItem>(res, ['types']);
}

/** Cars (type 1) marks for the models filter — legacy `getMarks(token, 1)`. */
export async function fetchCarMarks() {
  const res = await postForm<ListResponse>('/car/mark/list/', { type_id: 1, page: 0 });
  return getListRows<PlaceItem>(res, ['marks']);
}

const sitemapEndpoints = {
  city: { add: '/seo/city/add-to-sitemap/', remove: '/seo/city/remove-from-sitemap/' },
  brand: { add: '/seo/car/add-mark-to-sitemap/', remove: '/seo/car/remove-mark-from-sitemap/' },
  model: { add: '/seo/car/add-model-to-sitemap/', remove: '/seo/car/remove-model-from-sitemap/' },
} as const;

export type SitemapEntity = keyof typeof sitemapEndpoints;

export const setSitemapActive = (entity: SitemapEntity, id: number, isActive: boolean) =>
  submitForm(sitemapEndpoints[entity][isActive ? 'add' : 'remove'], { ids: [id] });

// ---- work types tree -------------------------------------------------------

export async function fetchWorkSections() {
  const res = await postForm<ListResponse>('/seo/work-type/get-sections/', {});
  return getListRows<SeoWorkType>(res, ['seo_work_types']);
}

export async function fetchWorkChildren(parentId: number) {
  const res = await postForm<ListResponse>('/seo/work-type/get-children/', { parent_id: parentId });
  return getListRows<SeoWorkType>(res, ['work_types']);
}

export async function fetchWork(id: number) {
  const res = await postForm<{ work_type: SeoWorkType }>('/seo/work-type/view/', {
    seo_work_type_id: id,
  });
  return res.work_type;
}

export const createWork = (v: { name: string; slug: string; isActive: number; parentId: number }) =>
  submitForm('/seo/work-type/create/', {
    name: v.name,
    slug: v.slug,
    parent_id: v.parentId,
    is_active: v.isActive,
  });

export const editWork = (v: { id: number; name: string; slug: string; isActive: number }) =>
  submitForm('/seo/work-type/edit/', {
    seo_work_type_id: v.id,
    name: v.name,
    slug: v.slug,
    is_active: v.isActive,
  });

export const moveWork = (id: number, newParentId: number) =>
  submitForm('/seo/work-type/move/', { seo_work_type_id: id, new_parent_id: newParentId });

export async function transliterateWorkName(name: string) {
  const res = await submitForm<{ success: number; slug: string }>('/seo/work-type/transliterate/', {
    work_type_name: name,
  });
  return res.slug;
}
